import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { meatProducts, meatCategories } from '@/mocks/meats';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function CatalogSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal(0.1);
  const [activeCategory, setActiveCategory] = useState('todos');

  const filteredProducts =
    activeCategory === 'todos'
      ? meatProducts
      : meatProducts.filter((p) => p.category === activeCategory);

  return (
    <section id="catalogo" className="py-16 md:py-24 bg-brand-dark">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          ref={ref}
          className={`flex flex-col items-center text-center md:flex-row md:items-end md:text-left md:justify-between gap-6 mb-12 transition-all duration-700 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <span className="inline-block px-4 py-1.5 border border-brand-gold/30 text-brand-gold text-sm rounded-full mb-4">
              {t('catalog_badge')}
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {t('catalog_title')}
            </h2>
          </div>
          <p className="text-white/50 text-sm md:text-base max-w-md">
            {t('catalog_desc')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-3 mb-10">
          {meatCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 md:px-5 md:py-2.5 text-sm font-medium rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-[1.03] ${
                activeCategory === cat.id
                  ? 'bg-brand-gold border-brand-gold text-brand-dark'
                  : 'border-white/20 text-white/70 hover:border-brand-gold/50 hover:text-white'
              }`}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>

        {/* Price Disclaimer */}
        <div className="flex items-center gap-2 mb-8 px-4 py-3 bg-brand-gold/10 border border-brand-gold/20 rounded-lg">
          <div className="w-5 h-5 flex items-center justify-center text-brand-gold shrink-0">
            <i className="ri-information-line text-base" />
          </div>
          <p className="text-white/70 text-xs md:text-sm leading-relaxed">
            {t('catalog_price_disclaimer')}
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 justify-items-center">
          {filteredProducts.map((product, idx) => (
            <div
              key={product.id}
              className={`group bg-brand-cream rounded-xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                product.comingSoon ? 'opacity-70' : ''
              }`}
              data-product-shop
              style={{
                transitionDelay: `${idx * 80}ms`,
              }}
            >
              {/* Image */}
              <div className="relative aspect-square overflow-hidden bg-gray-100">
                <img
                  src={product.image}
                  alt={t(product.nameKey)}
                  className={`w-full h-full object-cover object-top ${
                    product.comingSoon
                      ? 'grayscale'
                      : 'group-hover:scale-105'
                  } transition-transform duration-500`}
                />
                {product.badgeKey && !product.comingSoon && (
                  <div className="absolute top-3 left-3 px-3 py-1 bg-brand-red text-white text-xs font-semibold rounded-full">
                    {t(product.badgeKey)}
                  </div>
                )}
                {product.comingSoon && (
                  <div className="absolute inset-0 bg-brand-dark/60 flex items-center justify-center">
                    <div className="px-4 py-2 bg-brand-gold text-brand-dark text-sm font-bold rounded-full">
                      {t('catalog_coming_soon')}
                    </div>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4 md:p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-display text-base md:text-lg font-bold text-brand-dark">
                    {t(product.nameKey)}
                  </h3>
                </div>

                <p className="text-brand-dark/60 text-xs md:text-sm mb-3 line-clamp-2">
                  {t(product.descKey)}
                </p>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-brand-red font-bold text-lg md:text-xl">
                      ${product.price.toFixed(2)}
                    </span>
                    <span className="text-brand-dark/40 text-xs ml-1">
                      /{product.priceUnit}
                    </span>
                  </div>
                  <span className="text-brand-dark/50 text-xs">
                    {product.weight}
                  </span>
                </div>

                <div className="mt-3 pt-3 border-t border-brand-dark/10 flex items-center justify-between">
                  <span className="text-brand-dark/40 text-xs">
                    {product.origin}
                  </span>
                  {!product.comingSoon ? (
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-dark/5 text-brand-dark hover:bg-brand-red hover:text-white transition-all duration-300 cursor-pointer hover:scale-110">
                      <i className="ri-add-line" />
                    </button>
                  ) : (
                    <span className="text-brand-dark/30 text-xs">
                      <i className="ri-time-line mr-1" />
                      Soon
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16 text-white/50">
            <i className="ri-shopping-basket-line text-4xl mb-3 block" />
            <p>{t('catalog_empty')}</p>
          </div>
        )}
      </div>
    </section>
  );
}